import db from '../../versa/db.js';
import {
	STORAGE_ARM_BANNER_TEXT,
	collectStorageArmCodesFromLines,
	hasMixedStorageArmCodes,
	itemsHaveMixedStorageArm,
	storageArmCodeFromLabel,
	storageArmCodeFromProduct,
	storageArmLabel,
} from '../utils/storage-arm-type.js';

function _parseSialNum(s)
{
	const c = String(s ?? '').replace(/\./g, '').replace(',', '.');
	const n = parseFloat(c);
	return isFinite(n) ? n : 0;
}

function _resolveCompanyId(root)
{
	const el = root?.querySelector?.('#company_id');
	const raw = el?.value ?? '';
	const v = String(raw || '').trim();
	if (/^[0-9a-f-]{36}$/i.test(v))
		return v;
	const data = el?.getAttribute?.('data-value');
	if (data && /^[0-9a-f-]{36}$/i.test(String(data).trim()))
		return String(data).trim();
	return v || null;
}

function _resolveProductIdFromField(prodEl)
{
	if (!prodEl)
		return null;
	const data = prodEl.getAttribute?.('data-value');
	if (data != null && String(data).trim() !== '')
	{
		const n = Number(String(data).trim());
		if (Number.isInteger(n) && n > 0)
			return n;
	}
	const raw = String(prodEl.value || '').trim();
	if (!raw)
		return null;
	if (/^\d+$/.test(raw))
		return Number(raw);
	return null;
}

function _applyArmCellStyle(armEl, code)
{
	if (!armEl)
		return;
	armEl.classList.remove('oo-arm--clim', 'oo-arm--refrig');
	const td = armEl.closest('td');
	td?.classList.remove('oo-arm--clim', 'oo-arm--refrig');
	if (code === 'REFRIGERADO')
	{
		armEl.classList.add('oo-arm--refrig');
		td?.classList.add('oo-arm--refrig');
	}
	else if (code === 'CLIMATIZADO')
	{
		armEl.classList.add('oo-arm--clim');
		td?.classList.add('oo-arm--clim');
	}
}

/**
 * Atualiza estilos da coluna Arm. e o banner de mistura.
 * @param {Element|null} root
 */
export function paintOutputOrderStorageArmBanner(root)
{
	if (!root)
		return;
	const banner = root.querySelector('#oo-storage-arm-banner');
	const lines = [];
	const tbody = root.querySelector('.sial-items-scroll tbody');
	if (tbody)
	{
		for (const row of tbody.querySelectorAll('tr.row'))
		{
			const prodEl = row.querySelector('[id^="product_id-"]');
			if (!prodEl)
				continue;
			const n = (prodEl.id.match(/\d+$/) || ['0'])[0];
			const armEl = row.querySelector(`#storage_arm-${n}`);
			const armVal = armEl?.value;
			const code = storageArmCodeFromLabel(armVal);
			_applyArmCellStyle(armEl, code);
			const qty = _parseSialNum(row.querySelector(`#quantity-${n}`)?.value);
			lines.push({ quantity: qty, storage_arm: armVal });
			row.classList.toggle('oo-row--arm-mixed', false);
		}
	}
	const codes = collectStorageArmCodesFromLines(lines);
	const mixed = hasMixedStorageArmCodes(codes);
	if (tbody && mixed)
	{
		for (const row of tbody.querySelectorAll('tr.row'))
		{
			const prodEl = row.querySelector('[id^="product_id-"]');
			if (!prodEl)
				continue;
			const n = (prodEl.id.match(/\d+$/) || ['0'])[0];
			const qty = _parseSialNum(row.querySelector(`#quantity-${n}`)?.value);
			const code = storageArmCodeFromLabel(row.querySelector(`#storage_arm-${n}`)?.value);
			if (qty > 0 && code)
				row.classList.add('oo-row--arm-mixed');
		}
	}
	if (!banner)
		return;
	if (!mixed)
	{
		banner.style.display = 'none';
		banner.textContent = '';
		banner.classList.remove('oo-storage-arm-banner--warn');
		return;
	}
	banner.classList.add('oo-storage-arm-banner--warn');
	banner.style.display = 'block';
	banner.textContent = STORAGE_ARM_BANNER_TEXT;
}

/**
 * @param {string} companyId
 * @param {Array<{product_id?: *, quantity?: *}>} items
 * @returns {Promise<boolean>}
 */
export async function outputOrderItemsHaveMixedStorageArm(companyId, items)
{
	if (!companyId || !Array.isArray(items))
		return false;
	const map = new Map();
	for (const item of items)
	{
		const pid = Number(item?.product_id);
		const qty = Number(item?.quantity);
		if (!Number.isInteger(pid) || pid <= 0 || !(qty > 0) || map.has(pid))
			continue;
		let product = null;
		try
		{
			product = await db.get('products', [companyId, pid]);
		}
		catch (_e)
		{
			product = null;
		}
		map.set(pid, product);
	}
	return itemsHaveMixedStorageArm(items, map);
}

/**
 * Preenche campos Arm. das linhas a partir do cache de produtos.
 * @param {Element|null} root
 * @param {string|null} [companyIdOverride]
 */
export async function refreshOutputOrderStorageArmCells(root, companyIdOverride = null)
{
	if (!root)
		return;
	const companyId = companyIdOverride || _resolveCompanyId(root);
	const tbody = root.querySelector('.sial-items-scroll tbody');
	if (!tbody)
		return;
	for (const row of tbody.querySelectorAll('tr.row'))
	{
		const prodEl = row.querySelector('[id^="product_id-"]');
		if (!prodEl)
			continue;
		const n = (prodEl.id.match(/\d+$/) || ['0'])[0];
		const armEl = row.querySelector(`#storage_arm-${n}`);
		if (!armEl)
			continue;
		const pid = _resolveProductIdFromField(prodEl);
		if (!companyId || !pid)
		{
			if (!String(armEl.value || '').trim())
				armEl.value = '';
			continue;
		}
		// Recalcula Arm. pelo cache; se o produto ainda não chegou, não sobrescreve o valor da API (#MT-2F9C150)
		let product = null;
		try
		{
			product = await db.get('products', [companyId, pid]);
		}
		catch (_e)
		{
			product = null;
		}
		const code = storageArmCodeFromProduct(product);
		if (code)
			armEl.value = storageArmLabel(code);
		else if (!storageArmCodeFromLabel(armEl.value))
			armEl.value = '';
	}
	paintOutputOrderStorageArmBanner(root);
}
